import clsx from 'clsx';
import { ComponentProps, CSSProperties, PropsWithChildren, ReactNode } from 'react';
import useDataTestId from '~/hooks/useDataTestId';
import IconWarning from '~/icons/icon-alert-triangle-fill.svg?react';
import IconSuccess from '~/icons/icon-check-round-fill.svg?react';
import IconError from '~/icons/icon-close-round-fill.svg?react';
import IconInfo from '~/icons/icon-info-round-fill.svg?react';
import styles from './AlertMessage.module.scss';
import Icon from './Icon';
import Text from '../typographies/Text';

interface AlertMessageProps extends PropsWithChildren {
  action?: ReactNode;
  box?: boolean;
  className?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium' | 'large';
  style?: CSSProperties;
  type?: 'error' | 'info' | 'success' | 'warning';
  iconClassName?: string;
  dataTestId?: string;
  icon?: ComponentProps<typeof Icon>['component'];
}

const resolveIcon = (type: AlertMessageProps['type']) => {
  switch (type) {
    case 'success':
      return IconSuccess;
    case 'warning':
      return IconWarning;
    case 'error':
      return IconError;
    default:
      return IconInfo;
  }
};

const AlertMessage: React.FC<AlertMessageProps> = (props) => {
  const {
    box,
    className,
    fullWidth,
    size = 'medium',
    style,
    type = 'info',
    children,
    action,
    dataTestId,
    iconClassName,
    icon,
  } = props;
  const dt = useDataTestId(dataTestId);

  return (
    <div
      role="alert"
      className={clsx(
        styles.wrapper,
        styles[size],
        styles[type],
        {
          [styles.fullWidth!]: fullWidth,
          [styles.withoutBox!]: !box,
        },
        className,
      )}
      data-testid={dt()}
      style={style}
    >
      <Icon component={icon ?? resolveIcon(type)} className={clsx(styles.icon, iconClassName)} />
      {size === 'small' && (
        <Text variant="caption" weight="medium" className={styles.content}>
          {children}
        </Text>
      )}
      {size === 'medium' && (
        <Text variant="body2" weight="regular" className={styles.content}>
          {children}
        </Text>
      )}
      {size === 'large' && (
        <Text variant="body1" weight="regular" className={styles.content}>
          {children}
        </Text>
      )}
      {action && <div className={styles.action}>{action}</div>}
    </div>
  );
};

export default AlertMessage;
